import type { ComponentProps } from 'react'
import React, { useContext, useMemo } from 'react'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import type { OccupancyChildVariant } from 'components/Common/OccupancyPicker/OccupancyItem'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import config from 'constants/config'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import Group from 'components/utils/Group'
import FormattedOccupancy from 'components/Common/OccupancyPicker/FormattedOccupancy'
import BodyText from 'LuxKit/Typography/BodyText'
import { useIntl } from 'react-intl'

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'anchorRef' | 'triggerRef' | 'open' | 'onClose'> {
  rooms: Array<App.Occupants>
  isLastStep?: boolean
  saleUnit?: string
  childVariant?: OccupancyChildVariant
  onRoomChange: (rooms: Array<App.Occupants>) => void
  onRoomsApply: () => void
}

function SearchTravellersDropdown(props: Props) {
  const {
    open,
    triggerRef,
    anchorRef,
    rooms,
    onRoomChange,
    onRoomsApply,
    isLastStep,
    saleUnit,
    childVariant,
    onClose,
  } = props
  const { disableMultiRoom, disableSaleUnit } = useContext(GlobalSearchStateContext)
  const intl = useIntl()

  const ctaText = useMemo(() => {
    if (isLastStep) {
      return intl.formatMessage({
        id: 'ACiRzA1YGu',
        defaultMessage: 'Search',
        description: 'Search button text for global hotels search',
      })
    }
    return intl.formatMessage({
      id: '46hH4wAv7g',
      defaultMessage: 'Apply',
      description: 'Apply button text for search form panels',
    })
  }, [intl, isLastStep])

  return <DropdownSheet
    size="fill-anchor"
    anchorRef={anchorRef}
    triggerRef={triggerRef}
    open={open}
    primaryActionProps={{
      children: ctaText,
      type: 'button',
      onClick: onRoomsApply,
    }}
    footerStart={<Group direction="horizontal" horizontalAlign="end">
      <BodyText variant="large">
        <FormattedOccupancy
          occupancies={rooms}
          saleUnit={!disableSaleUnit ? saleUnit : undefined}
        />
      </BodyText>
    </Group>}
    onClose={onClose}
  >
    <OccupancyPicker
      saleUnit={saleUnit}
      occupancies={rooms}
      onChange={onRoomChange}
      enableMultiRoom={disableMultiRoom ? false : config.accommodation.allowsMultiRoom}
      disableSaleUnit={disableSaleUnit}
      childVariant={childVariant}
    />
  </DropdownSheet>
}

export default SearchTravellersDropdown
